import React from "react";
import { Dots, OVERLAY_CSS, useOverlayRun } from "./ProductOverlay";

/**
 * Product mode's export animation: an export card over the dimmed sheet.
 * File exports start once the card is gone (see useProductMode.playExport),
 * so the browser's download prompt follows the confirmation.
 *
 * Deliberately nothing like the intro: no brand, no wall of images. A
 * progress ring fills around a download arrow under "Exporting N items…",
 * then the ring completes and a big green check pops in with "Exported".
 * The line beneath names the destination per format. Tapping anywhere
 * skips.
 *
 * @param {number} total Products in the table
 * @param {string} format Export format key from useCloudExport
 * @param {number} durationMs Full run, from mount to the start of the fade
 * @param {() => void} [onLeave] Fired as the fade-out starts
 * @param {() => void} onDone Fired when the overlay can unmount
 */

const RING_RADIUS = 52;
const RING_CIRCUMFERENCE = Math.round(2 * Math.PI * RING_RADIUS * 100) / 100;

export const EXPORT_FORMATS = {
  csv: { detail: "CSV file" },
  excel: { detail: "Excel workbook" },
  json: { detail: "JSON file" },
  shopify: { detail: "Shopify CSV" },
  clipboard: { detail: "to the clipboard" },
  googlesheets: { detail: "to Google Sheets" },
};

const formatMeta = (format) => EXPORT_FORMATS[String(format || "").toLowerCase()] || EXPORT_FORMATS.csv;

const CSS = `
.pm-dl-card { animation: pm-dl-card-in 320ms cubic-bezier(.2,.9,.3,1.15) both; }
@keyframes pm-dl-card-in { from { opacity: 0; transform: translateY(10px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
.pm-dl-ring { stroke-dasharray: ${RING_CIRCUMFERENCE}; stroke-dashoffset: ${RING_CIRCUMFERENCE}; animation: pm-dl-ring linear both; transition: stroke 300ms; }
@keyframes pm-dl-ring { from { stroke-dashoffset: ${RING_CIRCUMFERENCE}; } to { stroke-dashoffset: 0; } }
.pm-dl-arrow { animation: pm-dl-bob 1.1s ease-in-out infinite; }
@keyframes pm-dl-bob { 0%, 100% { transform: translateY(-3px); } 50% { transform: translateY(4px); } }
.pm-dl-disc { animation: pm-dl-pop 520ms cubic-bezier(.2,.9,.3,1.35) both; }
@keyframes pm-dl-pop { 0% { transform: scale(.3); opacity: 0; } 60% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
.pm-dl-check { stroke-dasharray: 100; stroke-dashoffset: 100; animation: pm-dl-draw 420ms ease-out 180ms both; }
@keyframes pm-dl-draw { to { stroke-dashoffset: 0; } }
.pm-dl-burst { animation: pm-dl-burst 900ms ease-out both; }
@keyframes pm-dl-burst { 0% { box-shadow: 0 0 0 0 rgba(52,211,153,.55); } 100% { box-shadow: 0 0 0 36px rgba(52,211,153,0); } }
.pm-dl-swap { animation: pm-dl-swap 320ms ease-out both; }
@keyframes pm-dl-swap { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@media (prefers-reduced-motion: reduce) {
  .pm-dl-card, .pm-dl-disc, .pm-dl-swap { animation: none; opacity: 1; transform: none; }
  .pm-dl-arrow, .pm-dl-burst { animation: none; }
  .pm-dl-check { animation: none; stroke-dashoffset: 0; }
}
`;

const ProductExport = ({ total, format, durationMs, onLeave, onDone }) => {
  const productCount = Math.max(0, Number(total) || 0);
  const meta = formatMeta(format);
  const { phase, isComplete, landingMs, leave } = useOverlayRun({ durationMs, total: productCount, onLeave, onDone });
  const noun = productCount === 1 ? "item" : "items";

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Export in progress"
      onClick={leave}
      className={`fixed inset-0 z-[150] flex flex-col items-center justify-center bg-black/65 backdrop-blur-md text-white select-none ${
        phase === "leaving" ? "pm-overlay-leave" : "pm-overlay-enter"
      }`}
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <style>
        {OVERLAY_CSS}
        {CSS}
      </style>

      <div className="pm-dl-card relative w-72 max-w-[calc(100%-3rem)] rounded-3xl border border-white/[0.08] bg-[#111113]/95 px-6 pt-8 pb-7 flex flex-col items-center text-center shadow-2xl shadow-black/60">
        {/* Progress ring around the arrow; a green disc with a check once done */}
        <div className={`relative w-32 h-32 rounded-full ${isComplete ? "pm-dl-burst" : ""}`}>
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 120 120" aria-hidden="true">
            <defs>
              <linearGradient id="pm-dl-aurora" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#60a5fa" />
                <stop offset="50%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#c084fc" />
              </linearGradient>
            </defs>
            <circle cx="60" cy="60" r={RING_RADIUS} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
            <circle
              cx="60"
              cy="60"
              r={RING_RADIUS}
              fill="none"
              stroke={isComplete ? "#34d399" : "url(#pm-dl-aurora)"}
              strokeWidth="6"
              strokeLinecap="round"
              className="pm-dl-ring"
              style={{ animationDuration: `${landingMs}ms` }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {isComplete ? (
              <div className="pm-dl-disc w-[88px] h-[88px] rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                <svg className="w-12 h-12" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                  <path
                    d="M12 25l9 9 16-18"
                    pathLength="100"
                    stroke="#ffffff"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="pm-dl-check"
                  />
                </svg>
              </div>
            ) : (
              <svg className="pm-dl-arrow w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-5-5m5 5l5-5M5 20h14" />
              </svg>
            )}
          </div>
        </div>

        {/* Text, re-keyed so the swap to "Downloaded" eases in */}
        <div key={isComplete ? "done" : "running"} className="pm-dl-swap mt-6">
          <div className="text-[22px] font-bold tracking-tight leading-snug">
            {isComplete ? (
              "Exported"
            ) : (
              // The dots stay glued to the last word, so a wrapped headline never
              // drops them onto a line of their own under the text.
              <>
                Exporting {productCount.toLocaleString()}{" "}
                <span className="whitespace-nowrap">
                  {noun}
                  <Dots />
                </span>
              </>
            )}
          </div>
          <div className="mt-1.5 text-sm text-gray-400">
            {isComplete ? `${productCount.toLocaleString()} ${noun} · ${meta.detail}` : meta.detail}
          </div>
        </div>
      </div>

      <div className="mt-5 text-[11px] text-gray-500">Tap to skip</div>
    </div>
  );
};

export default ProductExport;
